// vendor
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router";
import isEmpty from "lodash/isEmpty";
import { Container, Row, Col, Spinner } from "reactstrap";

// app
import PageLayout from "../../components/PageLayout";
import LookupAddressForm from "./LookupAddressForm";
import VerifyAddress from "./VerifyAddress";
import PreOrderFlowConfirmation from "./PreOrderFlowConfirmation";
import SEO from "../../components/SEO";
import routeSeo from "../../components/SEO/routeSeo";
import { Navbar } from "../../components/Navbar";
import { Footer as MainFooter } from "../../components/Footers";
import { VerifyPageWrap, VerifyHeroBand, VerifyMain } from "./styled";
import {
  getAddressSuggestions,
  getExternalAddressSuggestions,
  clearAddressSuggestions,
  setAddress,
  setUserEmail,
  setAssessmentFormValues,
  postAssessmentData,
  getPropertyByOrder,
  getPropertyById,
  getPropertyCurrentAssessment,
} from "../../redux/actions/assessment";
import { isPreOrderFlow } from "../../constants";

const FreeAssessmentPage = ({
  getAddressSuggestions,
  getExternalSuggestions,
  clearSuggestions,
  selectAddress,
  addressSuggestions,
  suggestionsFailed,
  selectedAddress,
  setAssessmentFormValues,
  postAssessmentData,
  isAddressSuggestionLoading,
  globalLoading,
  uuid,
  getPropertyAssessment,
  freeAssessmentFormValues,
  getPropertyByOrder,
  getPropertyById,
  setUserEmail,
  customerEmail,
  location,
  history,
}) => {
  const [assessmentStep, setAssessmentStep] = useState(0);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const queryUuid = queryParams.get("uuid");
    const email = queryParams.get("email");
    const prop_id = queryParams.get("prop_id");

    if (queryUuid) {
      getPropertyByOrder(queryUuid, handlePropertyError);
      setAssessmentStep(1);
    }

    if (email && !queryUuid) {
      setUserEmail(email);
    }

    if (prop_id && !queryUuid) {
      getPropertyById(prop_id, handlePropertyError);
      setAssessmentStep(1);
    }

    if (!isEmpty(selectedAddress)) {
      setAssessmentStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePropertyError = () => {
    history.push("/verify");
    setAssessmentStep(0);
  };

  const handleChangeStep = (step) => {
    setAssessmentStep(step);
  };

  const getTitle = () => {
    let title = "Get Your ";
    if (!(globalLoading || uuid)) {
      title += "Free ";
    }
    title += `Property ${isPreOrderFlow ? "Estimate" : "Assessment"}`;
    return title;
  };

  const commonProps = {
    onSubmit: handleChangeStep,
    selectAddress,
    selectedAddress,
    postAssessmentData,
    uuid,
    getPropertyAssessment,
    assessmentValues: freeAssessmentFormValues,
    setUserEmail,
    customerEmail,
  };

  const renderStep = () => {
    switch (assessmentStep) {
      case 0:
        return (
          <LookupAddressForm
            {...commonProps}
            getAddressSuggestions={getAddressSuggestions}
            getExternalSuggestions={getExternalSuggestions}
            addressSuggestions={addressSuggestions}
            suggestionsFailed={suggestionsFailed}
            isLoading={isAddressSuggestionLoading}
            clearSuggestions={clearSuggestions}
          />
        );
      case 1:
        return (
          <VerifyAddress
            {...commonProps}
            setAssessmentFormValues={setAssessmentFormValues}
            clearSuggestions={clearSuggestions}
          />
        );
      case 2:
        return (
          <PreOrderFlowConfirmation
            assessmentValues={freeAssessmentFormValues}
            selectedAddress={selectedAddress}
            onSubmit={handleChangeStep}
          />
        );
      default:
        break;
    }
  };

  // Step 1 (the consolidated /verify form) gets a custom
  // editorial layout — Navbar + cream PageWrap + small hero
  // band + a single panel — that mirrors the post-submit
  // PropertyDossierPage so the user feels they're inside one
  // continuous document.  Steps 0 (lookup) and 2 (pre-order
  // confirm) keep the original PageLayout chrome (which they
  // already fit comfortably within).
  if (assessmentStep === 1) {
    return (
      <>
        <SEO {...routeSeo.verify} />
        <Navbar whiteLogo={false} />
        <VerifyPageWrap>
          <VerifyHeroBand>
            <Container>
              <Row>
                <Col className="mx-auto" lg="9" md="11" sm="12">
                  <div className="hero-eyebrow">Your property brief</div>
                  <h1 className="hero-title">{getTitle()}</h1>
                  <p className="hero-lede">
                    Here's how your home compares to your neighborhood,
                    your ZIP, and your county — then confirm the
                    details below and we'll email your Free Assessment.
                  </p>
                </Col>
              </Row>
            </Container>
          </VerifyHeroBand>
          <VerifyMain>
            <Container>
              <Row>
                <Col className="mx-auto" lg="11" md="12">
                  {globalLoading ? (
                    <div style={{ textAlign: "center", padding: "3rem 1rem" }}>
                      <Spinner color="info" />
                    </div>
                  ) : (
                    renderStep()
                  )}
                </Col>
              </Row>
            </Container>
          </VerifyMain>
        </VerifyPageWrap>
        <MainFooter />
      </>
    );
  }

  return (
    <>
      <SEO {...routeSeo.verify} />
      <PageLayout
        titleText={getTitle()}
        loading={globalLoading}
        colProps={{ lg: "8", md: "10", sm: "12", xs: "12" }}
      >
        {renderStep()}
      </PageLayout>
    </>
  );
};

const mapStateToProps = (state) => ({
  addressSuggestions: state.freeAssessment.addressSuggestions,
  suggestionsFailed: state.freeAssessment.suggestionsFailed,
  selectedAddress: state.freeAssessment.selectedAddress,
  isAddressSuggestionLoading: state.freeAssessment.loading.addressSuggestions,
  customerEmail: state.freeAssessment.customerEmail,
  globalLoading: state.freeAssessment.globalLoading,
  uuid: state.freeAssessment.uuid,
  freeAssessmentFormValues: state.freeAssessment.freeAssessmentFormValues,
});

const mapDispatchToProps = (dispatch) => ({
  getAddressSuggestions: (str) => dispatch(getAddressSuggestions(str)),
  getExternalSuggestions: (str) => dispatch(getExternalAddressSuggestions(str)),
  clearSuggestions: () => dispatch(clearAddressSuggestions()),
  selectAddress: (address) => dispatch(setAddress(address)),
  setUserEmail: (email) => dispatch(setUserEmail(email)),
  setAssessmentFormValues: (values) => dispatch(setAssessmentFormValues(values)),
  postAssessmentData: (discountObject, isFree = false) => dispatch(postAssessmentData(discountObject, isFree)),
  getPropertyByOrder: (uuid, handleError) => dispatch(getPropertyByOrder(uuid, handleError)),
  getPropertyById: (propId, handleError) => dispatch(getPropertyById(propId, handleError)),
  getPropertyAssessment: () => dispatch(getPropertyCurrentAssessment()),
});

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default compose(withRedux, withRouter)(FreeAssessmentPage);
